import React from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import { DirectionsBusRounded } from '@material-ui/icons'
import parkingButtonPanelStyles from './parkingButtonPanelStyles'
import IconPicker, {
  CustomIcon,
} from '../../../../../apps/AppComponents/IconPicker/IconPicker'
import { fetchUserParkingPermitData } from '../../redux/myMapsSlice'
import CheckIsMobile from '../../../../../../../utils/isMobileHook'
import { PARKINGFILTERSELECT } from '../../../../utils/constants'
import { logMapEvent } from '../../../../utils/events'

export default function ParkingButtonPanel({
  updateShowLotLabels,
  showLotLabels,
  showBusStops,
  updateShowBusStops,
  isMobileWithoutBottomNav,
  selectedFilters,
  setSelectedFilters,
  additionalFilters,
  setAdditionalFilters,
  dispatch,
}) {
  const profile = useSelector((s) => s.myProfile)
  const isMobile = CheckIsMobile()
  const iconSize = isMobile ? 30 : 40
  const [showPermitMessageOverlay, updateShowPermitMessageOverlay] =
    React.useState(false)
  const classes = parkingButtonPanelStyles()
  const options = ['permit', 'Faculty/Staff', 'student']
  // Filter (4): Handicap, EV charging, Covered (Garage or Solar = true), Gated (Regular Gate = true)
  const names = ['handicapped', 'evCharging', 'covered', 'gated']
  const [showAdditionalSettings, updateShowAdditionalSettings] =
    React.useState(false)

  // This function now handles all parking-related button clicks
  const handleParkingButtonClick = (option) => {
    // If the clicked option is already selected, unselect it by setting the filter to 'none'.
    // Otherwise, select the new option.
    const newSelectedFilters = selectedFilters.includes(option)
      ? ['none']
      : [option]
    if (newSelectedFilters?.includes('permit')) {
      dispatch(fetchUserParkingPermitData(profile.data.netID)).then(
        (result) => {
          console.log('result ', result)
          if (result.payload?.garageIdInfo?.length === 0) {
            updateShowPermitMessageOverlay(true)
          } else {
            updateShowPermitMessageOverlay(false)
          }
        },
      )
    }
    dispatch(setSelectedFilters(newSelectedFilters))
    logMapEvent(dispatch, PARKINGFILTERSELECT, newSelectedFilters[0])
  }

  const handleAllParkingClick = () => {
    // This will clear all other filters, effectively showing all lots
    // It's a toggle: if all lots are already selected, this will deselect them
    // by setting the filters to a state that indicates no selection.
    if (selectedFilters.length === 0) {
      dispatch(setSelectedFilters(['none']))
    } else {
      dispatch(setSelectedFilters([]))
    }
    logMapEvent(dispatch, PARKINGFILTERSELECT, 'All Lots')
  }

  const handleAdditionalSettings = () => {
    updateShowAdditionalSettings(!showAdditionalSettings)
  }

  const isAllParkingActive =
    selectedFilters.length === 0 && !selectedFilters.includes('none')

  return (
    <>
      {selectedFilters?.includes('permit') && showPermitMessageOverlay ? (
        <Box component={Paper} className={classes.parkingPermitOverlay}>
          <Typography variant="subtitle1">
            Parking permit information for you was unavailable to present
            permitted lots. To purchase a parking permit click the button below.
          </Typography>
          <Button
            component="a"
            href={process.env.REACT_APP_PARKING_PERMIT_EXTERNAL_LINK}
            target="_blank"
            className={classes.purchaseButton}
          >
            Purchase Permit
          </Button>
        </Box>
      ) : null}
      <Box
        component={Paper}
        className={
          isMobileWithoutBottomNav
            ? classes.parkingButtonPanelContainerMobile
            : classes.parkingButtonPanelContainer
        }
      >
        {window.location.hostname === 'localhost' ? (
          <Tooltip
            placement="right"
            title={
              <Typography variant="subtitle2">
                {showLotLabels
                  ? 'Hide Missing Lots (dev only)'
                  : 'Show Missing Lots (dev only)'}
              </Typography>
            }
            arrow
          >
            <IconButton
              className={
                showLotLabels ? classes.enabledButton : classes.disabledButton
              }
              onClick={() => {
                updateShowLotLabels((state) => !state)
              }}
            >
              <CustomIcon
                imgSrc="./assets/images/svgs/parking-lot-icon.svg"
                size={iconSize}
              />
            </IconButton>
          </Tooltip>
        ) : null}

        <Tooltip
          placement="right"
          title={
            <Typography variant="subtitle2">
              {showBusStops ? 'Hide Bus Stops' : 'Show Bus Stops'}
            </Typography>
          }
          arrow
        >
          <IconButton
            aria-label={showBusStops ? 'Hide Bus Stops' : 'Show Bus Stops'}
            className={
              showBusStops
                ? clsx(classes.enabledButton, classes.busStopsButton)
                : clsx(classes.disabledButton, classes.disabledBusStopsButton)
            }
            onClick={() => {
              updateShowBusStops((state) => !state)
            }}
          >
            <DirectionsBusRounded
              htmlColor="#fff"
              fontSize="herit"
              style={{ fontSize: iconSize }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip
          placement="right"
          title={
            <Typography variant="subtitle2">
              {isAllParkingActive
                ? 'Hide All Parking Lots'
                : 'Show All Parking Lots'}
            </Typography>
          }
          arrow
        >
          <IconButton
            aria-label={
              isAllParkingActive
                ? 'Hide All Parking Lots'
                : 'Show All Parking Lots'
            }
            className={
              isAllParkingActive
                ? classes.enabledAllParkingLotsButton
                : classes.disabledAllParkingLotsButton
            }
            onClick={handleAllParkingClick}
          >
            <CustomIcon
              imgSrc="./assets/images/svgs/parking-lot-icon.svg"
              size={iconSize}
            />
          </IconButton>
        </Tooltip>

        {options.map((option) => (
          <Tooltip
            placement="right"
            key={option}
            title={
              <Typography variant="subtitle2">
                {selectedFilters.includes(option)
                  ? `Hide ${option === 'permit' ? 'my' : ''}${
                      option.charAt(0).toUpperCase() + option.slice(1)
                    } Lots`
                  : `Show ${option === 'permit' ? 'my' : ''}${
                      option.charAt(0).toUpperCase() + option.slice(1)
                    } Lots`}
              </Typography>
            }
            arrow
          >
            <IconButton
              aria-label={
                selectedFilters.includes(option)
                  ? `Hide ${option} Lots`
                  : `Show ${option} Lots`
              }
              className={
                selectedFilters.includes(option)
                  ? clsx(classes.enabledButton)
                  : clsx(classes.disabledButton)
              }
              onClick={() => handleParkingButtonClick(option)}
            >
              {option === 'Faculty/Staff' && (
                <CustomIcon
                  imgSrc="./assets/images/svgs/employee-parking-lots.svg"
                  size={iconSize}
                />
              )}
              {option === 'student' && (
                <CustomIcon
                  imgSrc="./assets/images/svgs/student-parking-lots.svg"
                  size={iconSize}
                />
              )}
              {option === 'permit' && (
                <CustomIcon
                  imgSrc="./assets/images/svgs/permit-parking-lots.svg"
                  size={iconSize}
                />
              )}
            </IconButton>
          </Tooltip>
        ))}
        <div style={{ position: 'relative' }}>
          <Tooltip
            placement="right"
            title={
              <Typography variant="subtitle2">
                {showAdditionalSettings
                  ? 'Hide Additional Settings'
                  : 'Show Additional Settings'}
              </Typography>
            }
            arrow
          >
            <IconButton
              aria-label={
                showAdditionalSettings
                  ? 'Hide Additional Settings'
                  : 'Show Additional Settings'
              }
              className={
                showAdditionalSettings
                  ? classes.enabledAdditionalSettingsParkingLotsButton
                  : classes.disabledAdditionalSettingsParkingLotsButton
              }
              onClick={handleAdditionalSettings}
            >
              <CustomIcon
                imgSrc="./assets/images/svgs/sliders_icon.svg"
                size={iconSize}
              />
            </IconButton>
          </Tooltip>
          {showAdditionalSettings ? (
            <AdditionalSettingsSelect
              additionalFilters={additionalFilters}
              setAdditionalFilters={setAdditionalFilters}
              names={names}
              classes={classes}
              showAdditionalSettings={showAdditionalSettings}
              dispatch={dispatch}
            />
          ) : null}
        </div>
      </Box>
    </>
  )
}

const AdditionalSettingsSelect = ({
  additionalFilters,
  setAdditionalFilters,
  dispatch,
  names,
  classes,
  //   showAdditionalSettings,
}) => {
  const [open, setOpen] = React.useState(false)
  const profile = useSelector((s) => s.myProfile)
  const { userParkingPermitData } = useSelector((s) => s.maps)

  const handleUpdateSelection = (e) => {
    const { value } = e.target
    if (value?.includes('permit') && userParkingPermitData?.length === 0) {
      dispatch(fetchUserParkingPermitData(profile.data.netID))
    }
    dispatch(setAdditionalFilters(value))
    logMapEvent(dispatch, PARKINGFILTERSELECT, value?.join(','))
  }

  const handleSelectNameAndIcon = (name) => {
    let iconName
    switch (name) {
      case 'handicapped':
        iconName = 'wheelchair'
        break
      case 'evCharging':
        iconName = 'charging-station'
        break
      case 'covered':
        iconName = 'solar-panel'
        break
      case 'gated':
        iconName = 'warehouse'
        break
      case 'permit':
        iconName = 'car'
        break
      default:
        iconName = 'warehouse'
    }
    return iconName
  }

  React.useEffect(() => {
    setOpen(true)
  }, [])

  return (
    <div className={classes.additionalSettingsContainer}>
      <FormControl className={classes.formControl} fullWidth>
        <Select
          labelId="additional-settings"
          id="additional-settings-checkbox"
          multiple
          value={additionalFilters}
          onChange={handleUpdateSelection}
          disableUnderline
          variant="standard"
          open={open}
          MenuProps={{ variant: 'menu' }}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderValue={(selected) => `${selected.length} selected`}
        >
          {names.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox checked={additionalFilters.indexOf(name) > -1} />
              <ListItemText
                primary={
                  <>
                    <IconPicker iconName={handleSelectNameAndIcon(name)} />
                    {'   '}
                    <span style={{ paddingLeft: 10 }}>{name}</span>
                  </>
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}
